import { useState, useRef, useCallback, useMemo } from "react";
import { MapPin, Navigation, Expand, Copy, Pencil, Sparkles, Plus, Link2Off, ImageIcon, Upload, Loader2, X, ExternalLink } from "lucide-react";
import FilePreview from "@/modern/components/file-preview";
import { useLocationPhotos } from "@/react/hooks/entityHooks/useLocationPhotos";
import { toast } from "sonner";
import { cn } from "@/modern/utils";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Button } from "@/modern/components/ui/button-shadcn";
import { CardTitle } from "@/modern/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { LightboxProvider, LightboxImageTrigger } from "@/modern/components/ui/lightbox";
import { EntityLocationProvider, useEntityLocationContext } from "./context/EntityLocationContext";
import { LocationReadinessBadge } from "./components/LocationReadinessBadge";
import { geoLocationButtonUrlHelper } from "@/other/utils/utils.jsx";
import { LOCATION_GEO_BUTTON_CONFIG } from "@/other/constants/AddressGeoButtonConfig.jsx";
import { GOOGLE_STATIC_MAP_API_KEY } from "@/googlePlacesIntegration.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { formatLocationLines } from "./utils/locationDisplay";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildStaticMapUrl(lat: number, lon: number) {
  const params = new URLSearchParams({
    center: `${lat},${lon}`,
    zoom: "17",
    size: "800x320",
    maptype: "roadmap",
    markers: `color:red|${lat},${lon}`,
    key: GOOGLE_STATIC_MAP_API_KEY,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

function buildGoogleMapsUrl(lat: number, lon: number) {
  const params = new URLSearchParams({
    api: "1",
    query: `${lat},${lon}`,
  });
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

// ─── FieldRow — null when value is empty ──────────────────────────────────────

interface FieldRowProps {
  label: string;
  value?: string | null;
  className?: string;
  aiGenerated?: boolean;
}

const FieldRow = ({ label, value, className, aiGenerated }: FieldRowProps) => {
  const { locationRecord, record: entityRecord } = useEntityLocationContext();
  const { editorHandler } = useSwivuBryntumContext();
  const isEmpty = !value?.trim();

  const openEditor = () => {
    if (locationRecord) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editorHandler.openEditor as any)(EDITOR_KEYS.locationEditor, null, { locationRecord, entityRecord }, { by: "recordType", useExisting: true });
    }
  };

  return (
    <div className={cn("group space-y-1", className)}>
      <div className="flex items-center gap-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        {aiGenerated && <Sparkles className="size-3 text-violet-400" />}
      </div>
      {isEmpty ? (
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-muted-foreground/50">Not set</p>
          {!aiGenerated && (
            <button
              onClick={openEditor}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
              aria-label={`Edit ${label}`}
            >
              <Pencil className="size-3" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-foreground leading-relaxed">{value}</p>
      )}
    </div>
  );
};

// ─── MiniMap — only renders when coordinates are present and image loads ──────

const MiniMap = () => {
  const { locationRecord } = useEntityLocationContext();
  const [imgFailed, setImgFailed] = useState(false);
  const lat = locationRecord?.latitude ?? null;
  const lon = locationRecord?.longitude ?? null;
  const hasCoords = lat !== null && lon !== null;

  if (!hasCoords || imgFailed) return null;

  const mapUrl = buildStaticMapUrl(lat, lon);
  const googleMapsUrl = buildGoogleMapsUrl(lat, lon);
  const mapAlt = `Map of ${locationRecord?.propertyName ?? ""}`;

  return (
    <div className="group relative h-[37%] min-h-32 max-h-72 w-full shrink-0 overflow-hidden border-b border-border">
      <img
        src={mapUrl}
        alt={mapAlt}
        onError={() => setImgFailed(true)}
        className="h-full w-full object-cover"
        loading="lazy"
      />

      {/* gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent" />

      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noreferrer"
        className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-md bg-card/90 px-3 py-1.5 text-xs font-medium text-foreground opacity-0 shadow-sm backdrop-blur-sm transition-opacity hover:bg-card focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background group-hover:opacity-100"
        aria-label="Open location in Google Maps"
      >
        <ExternalLink className="size-3.5" />
        Open in Maps
      </a>

      {/* coords overlay — bottom left */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md bg-card/80 px-2 py-1 text-xs text-foreground backdrop-blur-sm">
        <MapPin className="size-3.5 text-primary" />
        {lat.toFixed(4)}, {lon.toFixed(4)}
      </div>
    </div>
  );
};

// ─── TitleSection ────────────────────────────────────────────────────────────

const TitleSection = () => {
  const { locationRecord } = useEntityLocationContext();
  const { heading, subLine, copyText } = formatLocationLines(locationRecord);

  const handleCopy = async () => {
    if (!copyText) return;
    await navigator.clipboard.writeText(copyText);
    toast.success("Address copied");
  };

  return (
    <div className="min-w-0 space-y-1">
      {heading && (
        <div className="flex items-center gap-2.5 flex-wrap">
          <h2 className="text-2xl font-semibold text-foreground truncate flex-1">{heading}</h2>
          <LocationReadinessBadge locationRecord={locationRecord} />
        </div>
      )}
      {subLine && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span className="truncate">{subLine}</span>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={handleCopy}
            aria-label="Copy address"
          >
            <Copy className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── ExternalLinksStrip ───────────────────────────────────────────────────────

const ExternalLinksStrip = () => {
  const { locationRecord } = useEntityLocationContext();
  const lat = locationRecord?.latitude ?? null;
  const lon = locationRecord?.longitude ?? null;
  const hasCoords = lat !== null && lon !== null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {LOCATION_GEO_BUTTON_CONFIG.map(({ title, icon, url }) => {
        const href = hasCoords
          ? geoLocationButtonUrlHelper(url, { lat, lon })
          : undefined;

        return (
          <Tooltip key={title}>
            <TooltipTrigger asChild>
              <a
                href={href}
                target={href ? "_blank" : undefined}
                rel="noreferrer"
                onClick={(e) => !href && e.preventDefault()}
                aria-disabled={!href}
                className={cn(
                  "inline-flex items-center justify-center size-8 rounded-md bg-card text-muted-foreground transition hover:bg-accent hover:text-foreground",
                  !href && "opacity-40 cursor-not-allowed"
                )}
                aria-label={title}
              >
                <img src={icon} alt={title} className="size-4 object-contain" />
              </a>
            </TooltipTrigger>
            <TooltipContent>{title}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
};

// ─── LocationPhotosPanel ──────────────────────────────────────────────────────

const LocationPhotosPanel = () => {
  const { locationRecord } = useEntityLocationContext();
  const { user } = useSwivuBryntumContext();
  const isReadOnly = !user?.can?.(4);
  const { photos, isUploading, uploadPhotos, removePhoto } = useLocationPhotos(locationRecord);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [removingIds, setRemovingIds] = useState<Set<string | number>>(new Set());

  const lightboxImages = useMemo(
    () =>
      photos
        .filter((p) => !p.record?.isPhantom)
        .map((p) => ({
          src: p.originalImageUrl || p.thumbnailUrl || "",
          alt: p.name,
        }))
        .filter((img) => !!img.src),
    [photos]
  );

  const lightboxIndexById = useMemo(() => {
    const map = new Map<string | number, number>();
    let idx = 0;
    photos.forEach((p) => {
      if (p.record?.isPhantom) return;
      const src = p.originalImageUrl || p.thumbnailUrl;
      if (!src) return;
      map.set(p.id, idx);
      idx += 1;
    });
    return map;
  }, [photos]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isReadOnly) return;
      if (e.target.files?.length) {
        await uploadPhotos(e.target.files);
        e.target.value = "";
      }
    },
    [uploadPhotos, isReadOnly]
  );

  const handleRemove = useCallback(
    async (id: string | number) => {
      if (isReadOnly) return;
      setRemovingIds((prev) => new Set(prev).add(id));
      await removePhoto(id);
      setRemovingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    },
    [removePhoto, isReadOnly]
  );

  if (photos.length === 0 && !isUploading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ImageIcon className="size-4" />
        </span>
        <div className="space-y-0.5">
          <p className="text-xs font-medium text-foreground">No site photos yet</p>
          <p className="text-xs text-muted-foreground">Upload images of the location.</p>
        </div>
        <button
          type="button"
          onClick={() => uploadRef.current?.click()}
          disabled={isReadOnly}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors",
            isReadOnly ? "opacity-40 cursor-not-allowed" : "hover:bg-primary/90"
          )}
        >
          <Upload className="size-3" />
          Add photos
        </button>
        <input ref={uploadRef} type="file" className="hidden" multiple accept="image/*" onChange={handleUpload} disabled={isReadOnly} />
      </div>
    );
  }

  return (
    <LightboxProvider images={lightboxImages} allowZoom showThumbnails>
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          {photos.map((file) => {
            const index = lightboxIndexById.get(file.id);
            return (
              <div key={file.id} className="group relative aspect-square overflow-hidden rounded-md border border-border bg-muted">
                {file.record?.isPhantom ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    <span className="text-[10px]">Uploading…</span>
                  </div>
                ) : (
                  <>
                    {typeof index === "number" ? (
                      <LightboxImageTrigger index={index} asChild>
                        <div className="absolute inset-0 z-0 cursor-zoom-in">
                          <FilePreview
                            file={file}
                            src={file.originalImageUrl || file.thumbnailUrl}
                            className="w-full h-full object-cover"
                            showName={false}
                            openFileOnClick={false}
                            disableLoadingState
                            isThumbnail
                          />
                        </div>
                      </LightboxImageTrigger>
                    ) : (
                      <div className="absolute inset-0 z-0">
                        <FilePreview
                          file={file}
                          src={file.originalImageUrl || file.thumbnailUrl}
                          className="w-full h-full object-cover"
                          showName={false}
                          openFileOnClick={false}
                          disableLoadingState
                          isThumbnail
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleRemove(file.id);
                      }}
                      disabled={removingIds.has(file.id) || isReadOnly}
                      className="absolute top-1 right-1 z-10 flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow disabled:opacity-100"
                      aria-label="Remove photo"
                    >
                      {removingIds.has(file.id)
                        ? <Loader2 className="size-3 animate-spin" />
                        : <X className="size-3" />}
                    </button>
                  </>
                )}
              </div>
            );
          })}

          {/* Add-more cell */}
          <button
            type="button"
            onClick={() => uploadRef.current?.click()}
            disabled={isReadOnly}
            className={cn(
              "aspect-square rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 flex flex-col items-center justify-center gap-1 text-muted-foreground transition-colors",
              isReadOnly ? "opacity-40 cursor-not-allowed" : "hover:bg-muted/70"
            )}
          >
            {isUploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <Plus className="size-4" />
                <span className="text-[10px]">Add photo</span>
              </>
            )}
          </button>
        </div>
        <input ref={uploadRef} type="file" className="hidden" multiple accept="image/*" onChange={handleUpload} disabled={isReadOnly} />
      </div>
    </LightboxProvider>
  );
};

// ─── LocationTabs ─────────────────────────────────────────────────────────────

const LocationTabs = () => {
  const { locationRecord } = useEntityLocationContext();
  const { photos } = useLocationPhotos(locationRecord);

  const floorVal = [locationRecord?.floorOrLevel, locationRecord?.elevation].filter(Boolean).join(" · ") || null;

  return (
    <Tabs defaultValue="work" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="work" className="text-sm font-medium">Work area</TabsTrigger>
        <TabsTrigger value="access" className="text-sm font-medium">Access</TabsTrigger>
        <TabsTrigger value="guidance" className="text-sm font-medium">Guidance</TabsTrigger>
        <TabsTrigger value="photos" className="text-sm font-medium">Photos{photos.length > 0 ? ` (${photos.length})` : ""}</TabsTrigger>
      </TabsList>

      {/* Fixed-height wrapper prevents layout shifts when switching tabs */}
      <div className="min-h-[9.5rem]">

        {/* Work area */}
        <TabsContent value="work" className="mt-4 grid grid-cols-2 gap-4">
          <FieldRow label="Building" value={locationRecord?.buildingName} />
          <FieldRow label="Floor" value={floorVal} />
          <FieldRow label="Work zone" value={locationRecord?.workZoneName} />
        </TabsContent>

        {/* Access */}
        <TabsContent value="access" className="mt-4 space-y-4">
          <FieldRow label="Primary entry route" value={locationRecord?.primaryEntryRoute} />
          <FieldRow label="Staging" value={locationRecord?.stagingArea} />
        </TabsContent>

        {/* Guidance */}
        <TabsContent value="guidance" className="mt-4 space-y-4">
          <FieldRow label="Start here" value={locationRecord?.startHereNote} />
          <FieldRow label="Location summary" value={locationRecord?.locationSummary} aiGenerated />
          <FieldRow label="Workforce handoff note" value={locationRecord?.workforceHandoffNote} aiGenerated />
        </TabsContent>

        {/* Photos */}
        <TabsContent value="photos" className="mt-4">
          <LocationPhotosPanel />
        </TabsContent>

      </div>
    </Tabs>
  );
};

// ─── Card content ─────────────────────────────────────────────────────────────

const EntityLocationContent = ({ onClose, headerTitle }: { onClose?: () => void; headerTitle?: string }) => {
  const { hasLocation, locationRecord, record: entityRecord, initialAddress } = useEntityLocationContext();
  const { editorHandler, project } = useSwivuBryntumContext();

  const openLocationEditor = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (editorHandler.openEditor as any)(EDITOR_KEYS.locationEditor, null, { locationRecord, entityRecord, initialAddress }, { by: "recordType", useExisting: true });
  };

  const handleDelink = async () => {
    entityRecord.set("locationId", null);
    await project.sync();
    toast.success("Location removed");
  };

  return (
    <DashboardCard
      onClose={onClose}
      cardContentClassName={cn("p-0", hasLocation ? "overflow-y-auto" : "overflow-hidden")}
      header={
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-base flex items-center truncate min-w-0">
            <span className="truncate">{headerTitle ?? "Location"}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <ButtonGroup>
              {hasLocation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="entityHeader" size="icon" onClick={handleDelink}>
                      <Link2Off className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Remove from task</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="entityHeader" size="icon" className={cn(hasLocation && "w-auto px-3")} onClick={openLocationEditor}>
                    {hasLocation ? <><Expand className="size-4" /> Manage</> : <Plus className="size-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{hasLocation ? "Edit location details" : "Add location"}</TooltipContent>
              </Tooltip>
            </ButtonGroup>
          </div>
        </div>
      }
    >
      {hasLocation ? (
        <div className="flex h-full min-h-0 flex-col">
          <MiniMap />
          <div className="shrink-0 space-y-4 p-4">
            <TitleSection />
            <ExternalLinksStrip />
            <LocationTabs />
          </div>
        </div>
      ) : (
        <div className="m-4 flex h-[calc(100%-2rem)] min-h-0 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
          <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MapPin className="size-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">No location set</h3>
            <p className="text-xs text-muted-foreground">Add an address to unlock geo tools and field guidance.</p>
          </div>
          <Button className="inline-flex items-center gap-2" onClick={openLocationEditor}>
            <Navigation className="size-4" />
            Add location
          </Button>
        </div>
      )}
    </DashboardCard>
  );
};

// ─── Entry point ──────────────────────────────────────────────────────────────

export interface EntityLocationWidgetProps extends EntityWidgetProps {
  initialAddress?: string;
}

export const EntityLocationView = ({ record, initialAddress, onClose, headerTitle }: EntityLocationWidgetProps) => {
  return (
    <EntityLocationProvider record={record} initialAddress={initialAddress}>
      <EntityLocationContent onClose={onClose} headerTitle={headerTitle} />
    </EntityLocationProvider>
  );
};
